import { SlotDesign, CardSvgLayer, CardLayerCommon, CardGradient, pxPerMm, framedImageLayout, svgGlyphBox, gradientStopColor } from './design-data';

type FadeMode = NonNullable<CardLayerCommon['fade']>;

export interface LoadedImages { [sourceKey: string]: CanvasImageSource; }

// Minimal shape of the 2d context this needs — just measureText, so callers
// (and tests) can pass a fake without stubbing the whole CanvasRenderingContext2D.
export interface MeasuringContext { measureText(text: string): { width: number } }

// Wrap `content` into lines the same way Konva's Text shape does (konva/lib/
// shapes/Text.js `_setTextData`, wrap="word", no ellipsis — the editor never
// sets ellipsis). This is a faithful reimplementation, not an approximation,
// so print output matches the on-screen editor exactly:
//
//   - split on '\n' into paragraphs, each wrapped independently;
//   - for each paragraph too wide to fit, binary-search (over Unicode code
//     points, via Array.from) the longest prefix that fits maxWidthPx;
//   - back off that prefix to the last space-or-dash so words aren't split
//     (unless the character right after the prefix is itself a space/dash,
//     in which case the prefix already ends on a natural boundary); if the
//     prefix contains NO space/dash (a single word wider than the box), the
//     backoff is a no-op and the raw character-level prefix is used instead
//     — this is what gives long unbroken words a char-level break;
//   - the emitted line is trimEnd()'d, the remainder trimStart()'d before
//     continuing;
//   - after EVERY line is appended, if `(linesSoFar + 1) * lineHeightPx >
//     maxHeightPx` (no room for one more line), stop immediately — silent
//     truncation, no ellipsis, matching Konva with ellipsis() unset.
//
// Empty content ('') matches Konva's own behaviour: '' has no width, fits
// trivially, and is emitted as a single empty line, so the caller still gets
// one vertically-centered (empty) line rather than nothing at all — this
// mirrors what Konva's textArr ends up holding for an empty Text node.
export function wrapTextLines(
  ctx: MeasuringContext,
  content: string,
  maxWidthPx: number,
  maxHeightPx: number,
  lineHeightPx: number,
): string[] {
  const SPACE = ' ';
  const DASH = '-';
  const width = (s: string) => ctx.measureText(s).width;

  const paragraphs = content.split('\n');
  const lines: string[] = [];
  let currentHeightPx = 0;
  // Konva always emits at least the first line/chunk before ever checking
  // whether it fits vertically (the height check runs AFTER each push), so
  // there is no upfront "does even one line fit" bailout here — mirror that.
  const fitsMore = () => currentHeightPx + lineHeightPx <= maxHeightPx;

  outer: for (const paragraph of paragraphs) {
    let line = paragraph;
    let lineWidth = width(line);
    if (lineWidth <= maxWidthPx) {
      lines.push(line);
      currentHeightPx += lineHeightPx;
      if (!fitsMore()) break outer;
      continue;
    }
    // Too wide: repeatedly binary-search the longest fitting prefix, back it
    // off to a word/dash boundary, emit it, and continue with the remainder.
    while (line.length > 0) {
      const lineArray = Array.from(line);
      let low = 0;
      let high = lineArray.length;
      let match = '';
      let matchWidth = 0;
      while (low < high) {
        const mid = (low + high) >>> 1;
        const substr = lineArray.slice(0, mid + 1).join('');
        const substrWidth = width(substr);
        if (substrWidth <= maxWidthPx) {
          low = mid + 1;
          match = substr;
          matchWidth = substrWidth;
        } else {
          high = mid;
        }
      }
      if (!match) break; // can't fit even a single character — bail out
      const matchArray = Array.from(match);
      const nextChar = lineArray[matchArray.length];
      const nextIsSpaceOrDash = nextChar === SPACE || nextChar === DASH;
      let wrapIndex: number;
      if (nextIsSpaceOrDash && matchWidth <= maxWidthPx) {
        wrapIndex = matchArray.length;
      } else {
        const lastSpaceIndex = matchArray.lastIndexOf(SPACE);
        const lastDashIndex = matchArray.lastIndexOf(DASH);
        wrapIndex = Math.max(lastSpaceIndex, lastDashIndex) + 1;
      }
      let low2 = matchArray.length;
      if (wrapIndex > 0) {
        low2 = wrapIndex;
        match = lineArray.slice(0, low2).join('');
      }
      match = match.trimEnd();
      lines.push(match);
      currentHeightPx += lineHeightPx;
      if (!fitsMore()) break outer;

      line = lineArray.slice(low2).join('').trimStart();
      if (line.length > 0) {
        lineWidth = width(line);
        if (lineWidth <= maxWidthPx) {
          lines.push(line);
          currentHeightPx += lineHeightPx;
          if (!fitsMore()) break outer;
          break; // remainder fully consumed as its own line
        }
      }
      // else: line still too wide (or empty) — loop again over the remainder
    }
  }

  return lines;
}

// Build a CanvasGradient for `grad` spanning the box (x, y, w, h) in device px.
// `angle` is in degrees (0 = left→right, 90 = top→bottom). The gradient line is
// centered on the box and projected so it spans the full box along that angle.
export function buildLinearGradient(
  ctx: CanvasRenderingContext2D,
  grad: CardGradient,
  x: number,
  y: number,
  w: number,
  h: number,
): CanvasGradient {
  const rad = (grad.angle * Math.PI) / 180;
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);
  // Half-extent of the projection of the box onto the gradient direction so the
  // start/end points sit on the box edges for any angle.
  const half = (Math.abs(dx) * w + Math.abs(dy) * h) / 2;
  const cx = x + w / 2;
  const cy = y + h / 2;
  const g = ctx.createLinearGradient(
    cx - dx * half,
    cy - dy * half,
    cx + dx * half,
    cy + dy * half,
  );
  for (const stop of grad.stops) {
    g.addColorStop(
      Math.max(0, Math.min(1, stop.offset)),
      gradientStopColor(stop.color),
    );
  }
  return g;
}

// Build a radial CanvasGradient for `gradient` centered on the box (x, y, w, h)
// in device px. The radius reaches the nearest edges of the box so the gradient
// achieves full transparency there; the canvas clamps stops beyond the radius to
// transparent, leaving no residual colour band at the mid-edges.
export function buildRadialGradient(
  ctx: CanvasRenderingContext2D,
  gradient: CardGradient,
  x: number,
  y: number,
  w: number,
  h: number,
): CanvasGradient {
  const cx = x + w / 2;
  const cy = y + h / 2;
  // Use min(w,h)/2 so the gradient reaches full transparency at the nearest
  // edges. Canvas clamps beyond the last stop to transparent → no colour band.
  const r = Math.min(w, h) / 2;
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
  for (const stop of gradient.stops) {
    g.addColorStop(
      Math.max(0, Math.min(1, stop.offset)),
      gradientStopColor(stop.color),
    );
  }
  return g;
}

// Dispatch to the right gradient builder based on gradient.type.
function buildGradient(
  ctx: CanvasRenderingContext2D,
  gradient: CardGradient,
  x: number,
  y: number,
  w: number,
  h: number,
): CanvasGradient {
  return gradient.type === 'radial'
    ? buildRadialGradient(ctx, gradient, x, y, w, h)
    : buildLinearGradient(ctx, gradient, x, y, w, h);
}

// Erase part of an already-drawn layer to transparent so the background shows
// through, in the chosen `mode` direction. `box = {left, top, width, height}`
// is the layer box in the CURRENT transform space. `strength` (0..1, clamped to
// [0.05, 1]) controls how far the fade reaches. Uses destination-out: an OPAQUE
// mask erases the drawn pixels, fading to no-erase (TRANSPARENT) where the
// element should stay. Must be called while still inside the same clip/transform
// as the drawn layer.
//
// Geometry (matches CanvasStage.FadeMask exactly), with s = clamped strength
// and f = clamped softness:
//   radial   — ELLIPTICAL vignette that reaches FULL transparency at every edge
//              (the gradient circle is scaled to the box, so it touches all four
//              edges — not only the corners). `s` sets the reach: kept solid
//              until radius (1 - s), fading to cleared at the edge. `f` sets the
//              softness: the clear→cleared blend spans the whole fade region at
//              f=1 (soft feather) or collapses to a sharp ring at the edge at
//              f=0. s=1 → fades from the very centre.
//   vertical — SYMMETRIC: OPAQUE at both top & bottom edges → TRANSPARENT centre
//              band. Each edge's fade spans s*0.5 of the height.
//   horizontal — SYMMETRIC across the width (left & right edges).
//   top      — OPAQUE at the top edge → TRANSPARENT at fraction s down.
//   bottom   — OPAQUE at the bottom edge → TRANSPARENT at fraction s up.
//   left     — OPAQUE at the left edge → TRANSPARENT at fraction s across.
//   right    — OPAQUE at the right edge → TRANSPARENT at fraction s across.
//   diagonal — OPAQUE at the top-right corner → TRANSPARENT at fraction s
//              toward the bottom-left along the diagonal.
function applyFade(
  ctx: CanvasRenderingContext2D,
  mode: FadeMode,
  box: { left: number; top: number; width: number; height: number },
  strength?: number,
  softness?: number,
): void {
  const { left, top, width: w, height: h } = box;
  if (w <= 0 || h <= 0) return;

  // Clamp strength to [0.05, 1]; default 0.5 when unset.
  const s = Math.max(0.05, Math.min(1, strength ?? 0.5));

  const opaque = 'rgba(0,0,0,1)'; // erases (destination-out)
  const clear = 'rgba(0,0,0,0)';  // keeps the pixel

  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';

  if (mode === 'radial') {
    // Elliptical gradient: draw a unit-radius radial gradient in a space scaled
    // to the box half-extents, so radius 1 lands on EVERY edge (corners sit at
    // radius >1 and clamp to fully cleared). This is what makes the fade reach
    // full transparency on all sides instead of only the corners.
    const cx = left + w / 2;
    const cy = top + h / 2;
    const f = Math.max(0, Math.min(1, softness ?? 0.5));
    const rStart = Math.max(0, 1 - s);                 // kept-solid radius
    const rKnee = Math.min(0.999, rStart + (1 - rStart) * (1 - f)); // softness knee
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(w / 2, h / 2);
    const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
    g.addColorStop(rStart, clear);
    if (rKnee > rStart + 1e-3) g.addColorStop(rKnee, clear);
    g.addColorStop(1, opaque);
    ctx.fillStyle = g;
    ctx.fillRect(-1, -1, 2, 2);
    ctx.restore();
  } else if (mode === 'verticalHorizontal') {
    // Combined ↕+↔: fade all four edges RECTANGULARLY (not radial). Draw the
    // vertical symmetric mask, then the horizontal one; destination-out
    // composites them so kept = (1-eraseV)·(1-eraseH) — corners clear most,
    // the centre band stays solid. Distinct from the elliptical radial vignette.
    const e = s * 0.5;
    const gv = ctx.createLinearGradient(left, top, left, top + h);
    gv.addColorStop(0, opaque);
    gv.addColorStop(e, clear);
    gv.addColorStop(1 - e, clear);
    gv.addColorStop(1, opaque);
    ctx.fillStyle = gv;
    ctx.fillRect(left, top, w, h);
    const gh = ctx.createLinearGradient(left, top, left + w, top);
    gh.addColorStop(0, opaque);
    gh.addColorStop(e, clear);
    gh.addColorStop(1 - e, clear);
    gh.addColorStop(1, opaque);
    ctx.fillStyle = gh;
    ctx.fillRect(left, top, w, h);
  } else {
    let g: CanvasGradient;
    if (mode === 'top') {
      g = ctx.createLinearGradient(left, top, left, top + h * s);
      g.addColorStop(0, opaque);
      g.addColorStop(1, clear);
    } else if (mode === 'bottom') {
      g = ctx.createLinearGradient(left, top + h, left, top + h * (1 - s));
      g.addColorStop(0, opaque);
      g.addColorStop(1, clear);
    } else if (mode === 'left') {
      g = ctx.createLinearGradient(left, top, left + w * s, top);
      g.addColorStop(0, opaque);
      g.addColorStop(1, clear);
    } else if (mode === 'right') {
      g = ctx.createLinearGradient(left + w, top, left + w * (1 - s), top);
      g.addColorStop(0, opaque);
      g.addColorStop(1, clear);
    } else if (mode === 'vertical') {
      // Symmetric ↕: clear at BOTH top & bottom edges, solid centre band. Each
      // edge's fade spans s*0.5 of the height. At s=1 the two clear stops meet.
      const e = s * 0.5;
      g = ctx.createLinearGradient(left, top, left, top + h);
      g.addColorStop(0, opaque);
      g.addColorStop(e, clear);
      g.addColorStop(1 - e, clear);
      g.addColorStop(1, opaque);
    } else if (mode === 'horizontal') {
      // Symmetric ↔: clear at BOTH left & right edges, solid centre band.
      const e = s * 0.5;
      g = ctx.createLinearGradient(left, top, left + w, top);
      g.addColorStop(0, opaque);
      g.addColorStop(e, clear);
      g.addColorStop(1 - e, clear);
      g.addColorStop(1, opaque);
    } else if (mode === 'diagonal2') {
      // diagonal2: top-left corner → bottom-right at fraction s of the diagonal.
      g = ctx.createLinearGradient(
        left, top,
        left + w * s, top + h * s,
      );
      g.addColorStop(0, opaque);
      g.addColorStop(1, clear);
    } else {
      // diagonal: top-right corner → bottom-left at fraction s of the diagonal.
      g = ctx.createLinearGradient(
        left + w, top,
        left + w * (1 - s), top + h * s,
      );
      g.addColorStop(0, opaque);
      g.addColorStop(1, clear);
    }
    ctx.fillStyle = g;
    ctx.fillRect(left, top, w, h);
  }

  ctx.restore();
}

// Draw the full design (background + layers in z-order) onto ctx at the given dpi.
// Images must be pre-loaded and passed in `images` keyed by layer.sourceKey.
//
// `options.includeBackground` (default true) controls whether the paper
// background (paper image / gradient / solid color) is painted. The PRINT
// export passes `false` so the rendered PDF/preview is transparent — the
// physical paper IS the background, so baking it in would double it.
//
// `options.backdropColorHex` (default undefined/off) is a SEPARATE, one-off
// full-canvas fill painted FIRST, beneath everything (including the
// `includeBackground` fill above). It exists only for the admin template
// thumbnail render: when a template has a showcase paper set, the thumbnail
// paints that paper's colour as its backdrop so the picker preview reflects
// the recommended paper. It is orthogonal to `includeBackground` and must
// never be set on the print-export path, which stays exactly as before
// (transparent, `includeBackground:false`).
export function drawSlotDesign(
  ctx: CanvasRenderingContext2D,
  design: SlotDesign,
  dpi: number,
  images: LoadedImages,
  emptyFrameLabel = '',
  options: { includeBackground?: boolean; backdropColorHex?: string } = {},
): void {
  const { includeBackground = true, backdropColorHex } = options;
  const ppm = pxPerMm(dpi);
  const cardW = design.canvas.widthMm * ppm;
  const cardH = design.canvas.heightMm * ppm;

  // Thumbnail backdrop: paint the full canvas with the showcase paper colour
  // FIRST, beneath the slot's own background fill and every layer. Off by
  // default — only the admin thumbnail render passes this.
  if (backdropColorHex) {
    ctx.fillStyle = backdropColorHex;
    ctx.fillRect(0, 0, cardW, cardH);
  }

  // Fill background (card area only, no bleed). Precedence: paper image →
  // gradient → solid colour. Skipped on print export (transparent paper) —
  // this is what keeps the paper image OUT of print files.
  if (includeBackground) {
    const bgImgCandidate = design.background.image
      ? images[design.background.image]
      : undefined;
    const bgEl = bgImgCandidate as unknown as {
      naturalWidth?: number;
      naturalHeight?: number;
      width?: number;
      height?: number;
    } | undefined;
    const bgNw = bgEl ? (bgEl.naturalWidth ?? bgEl.width ?? 0) : 0;
    const bgNh = bgEl ? (bgEl.naturalHeight ?? bgEl.height ?? 0) : 0;
    // A present-but-undecoded image (0x0) must fall through to the colour fill,
    // never produce NaN draw args and an unpainted card.
    const bgImg = bgNw > 0 && bgNh > 0 ? bgImgCandidate : undefined;
    if (bgImg) {
      // Cover: scale so the image fills the card, centre it, crop the overflow.
      const nw = bgNw;
      const nh = bgNh;
      const scale = Math.max(cardW / nw, cardH / nh);
      const dw = nw * scale;
      const dh = nh * scale;
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, cardW, cardH);
      ctx.clip();
      ctx.drawImage(bgImg, (cardW - dw) / 2, (cardH - dh) / 2, dw, dh);
      ctx.restore();
    } else {
      ctx.fillStyle = design.background.gradient
        ? buildGradient(ctx, design.background.gradient, 0, 0, cardW, cardH)
        : design.background.color;
      ctx.fillRect(0, 0, cardW, cardH);
    }
  }

  // Sort layers by z ascending
  const sorted = [...design.layers].sort((a, b) => a.z - b.z);

  for (const layer of sorted) {
    // Hidden layers are not rendered at all.
    if (layer.hidden) continue;
    ctx.save();
    // Per-layer opacity (0..1, default 1). save/restore above keeps it scoped.
    ctx.globalAlpha = layer.opacity ?? 1;
    if (layer.type === 'image') {
      const img = images[layer.sourceKey];
      const t = layer.transform;
      const cx = (t.x + t.width / 2) * ppm;
      const cy = (t.y + t.height / 2) * ppm;
      const w = t.width * ppm;
      const h = t.height * ppm;

      if (layer.frame) {
        // Framed image: position the box (bleed offset + center-translate + rotate),
        // then build a clip path from the frame (viewBox → box), clip, and either
        // draw the image cover-fit, or stroke the frame as an empty placeholder.
        const frame = layer.frame;
        ctx.translate(cx, cy);
        ctx.rotate(t.rotation * Math.PI / 180);
        // Move to the top-left of the box so the frame viewBox maps into it.
        ctx.translate(-w / 2, -h / 2);
        const sx = frame.viewBox.width > 0 ? w / frame.viewBox.width : 1;
        const sy = frame.viewBox.height > 0 ? h / frame.viewBox.height : 1;

        if (img) {
          // Clip to the frame shape in box pixel space. We bake the viewBox→box
          // scale into the path via a matrix so the image can be drawn in box px
          // (no need to inverse-scale the image afterwards).
          const clipBox = new Path2D();
          const m = new DOMMatrix([sx, 0, 0, sy, 0, 0]);
          for (const p of frame.paths) clipBox.addPath(new Path2D(p.d), m);
          ctx.clip(clipBox);

          // Fit the source image inside the box. Default = centred cover-fit;
          // `frameFit` (zoom/pan/rotate) is applied through the shared layout
          // helper so the print output matches the editor exactly.
          const imgEl = img as HTMLImageElement;
          const nw = imgEl.naturalWidth ?? imgEl.width ?? 1;
          const nh = imgEl.naturalHeight ?? imgEl.height ?? 1;
          const lay = framedImageLayout(nw, nh, w, h, layer.frameFit);
          ctx.save();
          ctx.translate(lay.cx, lay.cy);
          if (lay.rotationDeg) ctx.rotate((lay.rotationDeg * Math.PI) / 180);
          if (layer.flipH || layer.flipV) ctx.scale(layer.flipH ? -1 : 1, layer.flipV ? -1 : 1);
          ctx.drawImage(img, -lay.dw / 2, -lay.dh / 2, lay.dw, lay.dh);
          ctx.restore();
          // Fade: erase part of the image to transparent while still inside the
          // frame clip so the erase is clipped to the frame too. After the frame
          // translate the box top-left is (0,0), size w×h.
          if (layer.fade) applyFade(ctx, layer.fade, { left: 0, top: 0, width: w, height: h }, layer.fadeStrength, layer.fadeSoftness);
        } else {
          // Empty frame: subtle fill + outlined placeholder + centered hint so
          // the customer sees an empty photo slot to fill. The shape is drawn in
          // viewBox space; the hint is drawn in box-pixel space (centered).
          ctx.save();
          ctx.scale(sx, sy);
          for (const p of frame.paths) {
            const path = new Path2D(p.d);
            ctx.fillStyle = '#F1F5F9';
            ctx.fill(path);
            ctx.strokeStyle = '#94A3B8';
            ctx.lineWidth = 2;
            ctx.stroke(path);
          }
          ctx.restore();
          // Localized empty-frame hint (caller passes translated label; empty = no hint).
          if (emptyFrameLabel) {
            const hintPx = Math.max(8, Math.min(w, h) * 0.09);
            ctx.fillStyle = '#64748B';
            ctx.font = `${hintPx}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(emptyFrameLabel, w / 2, h / 2);
          }
        }
      } else {
        if (!img) { ctx.restore(); continue; }
        ctx.translate(cx, cy);
        ctx.rotate(t.rotation * Math.PI / 180);
        // Mirror about the centre inside its own save/restore so the fade below is
        // applied in un-flipped box space (matches the editor, where the fade mask
        // is a sibling of the flipped image).
        ctx.save();
        if (layer.flipH || layer.flipV) ctx.scale(layer.flipH ? -1 : 1, layer.flipV ? -1 : 1);
        if (layer.crop) {
          const c = layer.crop;
          // Use 9-arg drawImage with crop fractions
          // We need the natural image dimensions — use type assertion
          const imgEl = img as HTMLImageElement;
          const nw = imgEl.naturalWidth ?? imgEl.width ?? 1;
          const nh = imgEl.naturalHeight ?? imgEl.height ?? 1;
          ctx.drawImage(img,
            c.x * nw, c.y * nh, c.width * nw, c.height * nh,
            -w / 2, -h / 2, w, h
          );
        } else {
          ctx.drawImage(img, -w / 2, -h / 2, w, h);
        }
        ctx.restore();
        // Fade: box top-left is (-w/2, -h/2), size w×h in the current
        // translated+rotated space (both crop and plain variants).
        if (layer.fade) applyFade(ctx, layer.fade, { left: -w / 2, top: -h / 2, width: w, height: h }, layer.fadeStrength, layer.fadeSoftness);
      }
    } else if (layer.type === 'text') {
      const t = layer.transform;
      const sizePx = layer.size / 72 * dpi;
      const w = t.width * ppm;
      const h = t.height * ppm;
      const cx = (t.x + t.width / 2) * ppm;
      const cy = (t.y + t.height / 2) * ppm;
      ctx.translate(cx, cy);
      ctx.rotate(t.rotation * Math.PI / 180);
      // Quote the family (hardens multi-word names like "Playfair Display")
      // and prepend the real weight when bold — no more " Bold"-suffixed
      // family strings (see normalizeTextLayerFont in templates/types.ts).
      ctx.font = `${layer.bold ? '700 ' : ''}${sizePx}px "${layer.font}"`;
      // Gradient overrides the solid color. After the translate above the box is
      // centered on the origin, so span the gradient from -w/2,-h/2.
      ctx.fillStyle = layer.gradient
        ? buildLinearGradient(ctx, layer.gradient, -w / 2, -h / 2, w, h)
        : layer.color;
      ctx.textAlign = (layer.align ?? 'left') as CanvasTextAlign;
      ctx.textBaseline = 'middle';
      // Wrap exactly like the editor's Konva Text node (wrap="word", lineHeight
      // 1.0 — see wrapTextLines above) so print/preview/thumbnail match the
      // on-screen editor instead of dumping unwrapped text at 1.2x spacing.
      const maxW = t.width * ppm;
      const maxH = t.height * ppm;
      const lineH = sizePx; // Konva default lineHeight is 1.0
      const lines = wrapTextLines(ctx, String(layer.content), maxW, maxH, lineH);
      // Alignment anchor relative to the box, independent of ctx.textAlign's
      // own semantics: left starts at the box's left edge, right ends at the
      // box's right edge, center sits on the box's horizontal center.
      const anchorX = (layer.align === 'left' || !layer.align)
        ? -maxW / 2
        : layer.align === 'right' ? maxW / 2 : 0;
      lines.forEach((line, i) => {
        ctx.fillText(line, anchorX, (i - (lines.length - 1) / 2) * lineH);
      });
      // Fade: box is centered on the origin (-w/2, -h/2), size w×h.
      if (layer.fade) applyFade(ctx, layer.fade, { left: -w / 2, top: -h / 2, width: w, height: h }, layer.fadeStrength, layer.fadeSoftness);
    } else if (layer.type === 'shape') {
      const t = layer.transform;
      const cx = (t.x + t.width / 2) * ppm;
      const cy = (t.y + t.height / 2) * ppm;
      const w = t.width * ppm;
      const h = t.height * ppm;
      ctx.translate(cx, cy);
      ctx.rotate(t.rotation * Math.PI / 180);

      if (layer.shape === 'rect') {
        const rx = -w / 2;
        const ry = -h / 2;
        if (layer.fill || layer.fillGradient) {
          ctx.fillStyle = layer.fillGradient
            ? buildGradient(ctx, layer.fillGradient, rx, ry, w, h)
            : (layer.fill as string);
          if (layer.radiusMm && typeof (ctx as unknown as Record<string, unknown>).roundRect === 'function') {
            const r = layer.radiusMm * ppm;
            ctx.beginPath();
            (ctx as unknown as { roundRect: (x: number, y: number, w: number, h: number, r: number) => void })
              .roundRect(rx, ry, w, h, r);
            ctx.fill();
          } else {
            ctx.fillRect(rx, ry, w, h);
          }
        }
        if (layer.stroke && layer.strokeWidth) {
          ctx.strokeStyle = layer.stroke;
          ctx.lineWidth = layer.strokeWidth * ppm;
          ctx.strokeRect(rx, ry, w, h);
        }
      } else if (layer.shape === 'line') {
        // Treat the transform box as the line bounds: draw from left-center to right-center
        ctx.beginPath();
        ctx.moveTo(-w / 2, 0);
        ctx.lineTo(w / 2, 0);
        if (layer.stroke) ctx.strokeStyle = layer.stroke;
        if (layer.strokeWidth) ctx.lineWidth = layer.strokeWidth * ppm;
        ctx.stroke();
      }
      // Fade: box is centered on the origin (-w/2, -h/2), size w×h.
      if (layer.fade) applyFade(ctx, layer.fade, { left: -w / 2, top: -h / 2, width: w, height: h }, layer.fadeStrength, layer.fadeSoftness);
    } else if (layer.type === 'svg') {
      const svgLayer = layer as CardSvgLayer;
      const t = svgLayer.transform;
      const w = t.width * ppm;
      const h = t.height * ppm;
      // Translate to the center of the placement box (bleed offset + center of box)
      const cx = (t.x + t.width / 2) * ppm;
      const cy = (t.y + t.height / 2) * ppm;
      ctx.translate(cx, cy);
      ctx.rotate(t.rotation * Math.PI / 180);
      // Translate back to top-left of the box so scale maps viewBox → box
      ctx.translate(-w / 2, -h / 2);
      // Scale so the SVG viewBox maps exactly into the placement box
      ctx.scale(w / svgLayer.viewBox.width, h / svgLayer.viewBox.height);
      // Gradient (if set) spans the full viewBox so it covers all paths the same
      // way regardless of each path's bounds; coords are in viewBox space here.
      const svgGrad = svgLayer.gradient
        ? buildGradient(ctx, svgLayer.gradient, 0, 0, svgLayer.viewBox.width, svgLayer.viewBox.height)
        : null;
      // Pre-compute layer-level stroke width in viewBox units once (mm → viewBox).
      // The canvas is already scaled by w/viewBox.width, so to express mm as
      // viewBox units: strokeWidthVb = strokeMm * viewBox.width / widthMm.
      const layerStroke = svgLayer.stroke && svgLayer.strokeWidth
        ? {
            color: svgLayer.stroke,
            widthVb: svgLayer.strokeWidth * (svgLayer.viewBox.width / svgLayer.transform.width),
          }
        : null;
      for (const path of svgLayer.paths) {
        const p = new Path2D(path.d);
        ctx.fillStyle = svgGrad ?? svgLayer.color ?? path.fill ?? '#000000';
        ctx.fill(p);
        // Layer-level stroke takes precedence over per-path stroke when set.
        if (layerStroke) {
          ctx.strokeStyle = layerStroke.color;
          ctx.lineWidth = layerStroke.widthVb;
          ctx.lineJoin = 'round';
          ctx.lineCap = 'round';
          ctx.stroke(p);
        } else if (path.stroke && path.strokeWidth) {
          ctx.strokeStyle = path.stroke;
          // path.strokeWidth is already in viewBox units (the canvas is scaled)
          ctx.lineWidth = path.strokeWidth;
          ctx.stroke(p);
        }
      }
      // Fade: the canvas is translated to the box top-left and scaled so the
      // viewBox fills the box, so we erase in viewBox space. Fade over the
      // glyph's TIGHT bounds (not the padded viewBox) so a radial/symmetric fade
      // reaches transparency at the visible edges of the icon rather than at the
      // empty box margins — matches the editor's FadeMask box exactly.
      if (svgLayer.fade) {
        const gb = svgGlyphBox(svgLayer.paths, svgLayer.viewBox);
        applyFade(ctx, svgLayer.fade, gb ?? {
          left: 0,
          top: 0,
          width: svgLayer.viewBox.width,
          height: svgLayer.viewBox.height,
        }, svgLayer.fadeStrength, svgLayer.fadeSoftness);
      }
    }
    ctx.restore();
  }
}
